import { Router, ActivatedRoute } from '@angular/router';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { Component, Input, OnInit, Output, EventEmitter, OnChanges } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TreeNode, TreeDragDropService, ConfirmationService } from 'primeng/api';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { ElementModel } from 'projects/core/src/lib/models/element.model';
import { environment } from 'projects/environments/environment';
import { v4 as uuidv4 } from 'uuid';
import { TranslocoService } from '@ngneat/transloco';
import { ExerciseModel } from 'projects/core/src/lib/models/exercise.model';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';

@Component({
  selector: 'app-element-content',
  templateUrl: './element-content.component.html',
  styleUrls: ['./element-content.component.scss'],
  providers: [TreeDragDropService],
})
export class ElementContentComponent implements OnInit, OnChanges {

  @Input() exercise: ExerciseModel = null;
  @Input() element: ElementModel = null;
  @Output() setEdit = new EventEmitter<string>();

  public inEdit = false;
  public chapters: Array<TreeNode>;
  public activeNode: TreeNode = null;
  public messages: Array<any> = [];
  public baseUrl = '';
  public saveInheritVisible = false;
  public deleteInheritVisible = false;
  public deleteInheritChapter: TreeNode = null;

  public videosVisible = false;
  public videosLoading = false;
  public videos: Array<any> | null = null;

  public form: UntypedFormGroup = this.fb.group({
    chapter: new UntypedFormControl('', [Validators.required]),
    content: new UntypedFormControl('', [Validators.required]),
    autoplay: new FormControl(false),
  });

  constructor(
    private ui: UiService,
    private fb: UntypedFormBuilder,
    private api: ApiService,
    private router: Router,
    private route: ActivatedRoute,
    private confirmationService: ConfirmationService,
    private transloco: TranslocoService,
    public auth: AuthStateService,
  ) {
    this.baseUrl = environment.baseUrl;
  }

  ngOnInit(): void {
    this.ui.setFocus();
  }

  ngOnChanges(): void {
    this.chapters = [];
    if (this.element?.content !== null && this.element?.content?.length > 0) {
      this.element.content.forEach((node: any) => {
        this.chapters.push(this.mapNode(node));
      });
    }
    if (this.chapters.length > 0) {
      this.activeNode = this.chapters[0];
    }
  }

  public onNew(): void {
    if (this.auth.hasRight('admin.element.edit')) {
      this.activeNode = null;
      this.startEdit();
    }
  }

  public onEdit(): void {
    if (this.auth.hasRight('admin.element.edit')) {
      this.startEdit();
    }
  }

  public onDelete(chapter: TreeNode): void {
    if (this.auth.hasRight('admin.element.edit')) {
      this.confirmationService.confirm({
        message: this.transloco.translate('common.messages.really_delete', { value: chapter.label }),
        header: this.transloco.translate('common.really_delete'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.deleteInheritChapter = chapter;
          if (this.exercise?.shouldShowInheritDialog()) {
            this.deleteInheritVisible = true;
          } else {
            this.deleteChapter(false);
          }
        }
      });
    }
  }

  public deleteChapter(inherit: boolean = false): void {
    const index = this.chapters.findIndex((actual: TreeNode) => {
      return actual.key === this.deleteInheritChapter.key;
    });
    this.chapters.splice(index, 1);
    if (this.chapters.length > 0) {
      this.activeNode = this.chapters[Math.max(0, index - 1)];
    } else {
      this.activeNode = null;
    }
    this.onSaveContent(false, inherit);
    this.ui.toastSuccess(this.transloco.translate('common.messages.deleted', { value: this.deleteInheritChapter.label }), this.transloco.translate('common.deleted'));
  }

  public onCancel(): void {
    this.stopEdit();
  }

  public onSubmit(): void {
    this.ui.hideMessage();
    if (this.form.valid === true) {
      if (this.exercise?.shouldShowInheritDialog()) {
        this.saveInheritVisible = true;
      } else {
        this.saveChapter(false);
      }
    } else {
      this.ui.showMessage('error', this.transloco.translate('common.error_on_form_please_correct'), this.transloco.translate('common.error'));
      FormHelper.markFormAsDirty(this.form);
    }
  }

  public saveChapter(inherit: boolean = false): void {
    if (this.activeNode !== null) {
      this.activeNode.label = this.form.value.chapter;
      this.activeNode.data = this.form.value.content;
    } else {
      const newNode = {
        key: uuidv4(),
        label: this.form.value.chapter,
        data: this.form.value.content
      };
      this.chapters.push(newNode);
      this.activeNode = newNode;
    }
    this.onSaveContent(true, inherit);
    this.stopEdit();
  }

  public startEdit(): void {
    this.form.reset();
    if (this.activeNode !== null) {
      this.form.patchValue({
        chapter: this.activeNode.label,
        content: this.activeNode.data,
        autoplay: this.element.misc?.['autoplay'] === 1 ? true : false
      });
    } else {
      this.form.patchValue({
        chapter: '',
      });
    }
    this.inEdit = true;
    this.setChaptersStatus(this.chapters, false);
    this.setEdit.emit('content');
    this.ui.setFormFocus();
  }

  public stopEdit(): void {
    this.form.reset();
    this.inEdit = false;
    this.setChaptersStatus(this.chapters, true);
    this.setEdit.emit(null);
    this.ui.setFocus();
  }

  private setChaptersStatus(chapters: Array<TreeNode>, status: boolean): void {
    if (chapters?.length > 0) {
      chapters.forEach((chapter: TreeNode) => {
        chapter.selectable = status;
        this.setChaptersStatus(chapter.children, status);
      });
    }
  }

  public onNodeSelect(event: any): void {
    if (!this.inEdit) {
      this.activeNode = event.node;
    }
  }

  public onNodeDrop(event: any): void {
    this.onSaveContent(false);
  }

  public onShowVideoLinks(): void {
    this.videosVisible = true;
    this.videosLoading = true;
    this.api.getVideos().subscribe({
      next: (result: any) => {
        this.videos = result.data;
      },
      error: (error) => {
      }
    }).add(() => {
      this.videosLoading = false;
    });
  }

  public copyVideoUrl(url: string): void {
    this.ui.copyToClipboard(url);
    this.videosVisible = false;
    this.videos = null;
  }

  public getEditorInit(): any {
    return {
      height: 500,
      menubar: false,
      file_picker_types: 'image media',
      images_upload_handler: (blobInfo: any, progress: any) => {
        return new Promise((resolve, reject) => {
          this.api.uploadEditorImage(this.element.id, blobInfo.blob(), blobInfo.filename()).subscribe({
            next: (result: any) => {
              resolve(result.location);
            },
            error: (error) => {
              reject(error);
            }
          });
        });
      },
      video_template_callback: (data: any) =>
        `<video width="${data.width}" height="${data.height}"${data.poster ? ` poster="${data.poster}"` : ''} controls="controls" autoplay>\n` +
        `<source src="${data.source}"${data.sourcemime ? ` type="${data.sourcemime}"` : ''} />\n` +
        (data.altsource ? `<source src="${data.altsource}"${data.altsourcemime ? ` type="${data.altsourcemime}"` : ''} />\n` : '') +
        '</video>',
      automatic_uploads: true,
      plugins: [
        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview', 'anchor',
        'searchreplace', 'visualblocks', 'code', 'fullscreen',
        'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
      ],
      toolbar:
        'undo redo | blocks | bold italic forecolor backcolor | \
        alignleft aligncenter alignright alignjustify | \
        bullist numlist outdent indent | image media table | removeformat | fullscreen code',
      block_formats:
        'Paragraph=p;Heading 1=h1;Heading 2=h2;Heading 3=h3;Small=small;Preformatted=pre',
      formats: {
        small: { inline: 'small' },
        arrow: { selector : 'li', classes : 'full'}
      },
      advlist_bullet_styles: 'square disc',
      paste_preprocess: (editor, args) => {
        // PASTE / REPLACE TAGS AND ATTRIBUTES
      }
    };
  }

  private onSaveContent(showToast: boolean = true, inherit: boolean = false): void {
    this.element.content = this.remapNodes(this.chapters);
    const autoplay = this.form.value.autoplay === true ? 1 : 0;
    this.api.saveElementContent(this.element.id, this.element.content, autoplay, inherit).subscribe({
      next: (result: any) => {
        if (showToast === true) {
          this.ui.toastSuccess(this.transloco.translate('common.messages.saved', { value: this.element.name }), this.transloco.translate('common.saved'));
        }
        if (this.element.id !== result.data.id) {
          this.route.params.subscribe(params => {
            localStorage.setItem('element-tab-' + result.data.id, '1'); // save selected tab
            this.router.navigate(['/exercises', this.exercise.id, 'elements', result.data.id], { replaceUrl: true });
          });
        } else {
          this.element.deserialize(result.data);
        }
        this.stopEdit();
      },
      error: (error: any) => {
        this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
        FormHelper.setBackendErrors(this.form, error.fielderrors);
      }
    });
  }

  private mapNode(node: any): TreeNode {
    const children: Array<TreeNode> = [];
    if (node.children !== undefined && node.children !== null && node.children.length > 0) {
      node.children.forEach((child: any) => {
        children.push(this.mapNode(child));
      });
    }
    return {
      key: node.id,
      label: node.name,
      data: node.content,
      children
    };
  }

  private remapNodes(nodes: Array<TreeNode>): Array<any> {
    const result = [];
    nodes.forEach((node: TreeNode) => {
      result.push({
        id: node.key,
        name: node.label,
        content: node.data,
        children: (node.children !== null && node.children !== undefined && node.children.length > 0) ? this.remapNodes(node.children) : []
      });
    });
    return result;
  }

}
